import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bot, Phone, PhoneCall, Loader2, Eye, AlertTriangle, CheckCircle2, ShieldAlert, X,
  Link as LinkIcon, Copy, Check, ExternalLink,
} from "lucide-react";
import { getApiUrlsWithFallback } from "@/config/api";

const INK = "hsl(164 33% 7%)";
const TERMINAL = new Set(["completed", "error", "failed", "busy", "no-answer"]);

async function postFirst(path, body) {
  let last = null;
  for (const url of getApiUrlsWithFallback(path)) {
    try {
      const res = await fetch(url, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => null);
      last = { ok: res.ok, json };
      if (json) return last;
    } catch (e) { /* try next base */ }
  }
  return last || { ok: false, json: null };
}

/**
 * Internal console for Riya's Mode B outbound sales call. Dials a real phone, so:
 *  - "Preview" (dry run) shows the number + script and dials nothing.
 *  - "Place call" requires an explicit confirm and the server console token.
 * Not linked from public nav; gated by ZIA_CONSOLE_TOKEN on the backend.
 */
export default function ZiaCallConsole() {
  const [form, setForm] = useState({
    prospect_name: "", phone: "", referrer_name: "", prospect_email: "", company: "", token: "",
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");        // "preview" | "call" | ""
  const [error, setError] = useState("");
  const [call, setCall] = useState(null);      // { call_sid, status, ... }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pollRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const doPreview = async () => {
    setError(""); setCall(null); setBusy("preview");
    const { json } = await postFirst("/api/voice-demo/zia-call", { ...form, confirm: false });
    setBusy("");
    if (!json) { setError("Could not reach the server."); return; }
    if (!json.ok) { setError(json.error || "Preview failed."); return; }
    setPreview(json);
  };

  const pollStatus = (sid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      for (const url of getApiUrlsWithFallback(`/api/voice-demo/zia-call/${sid}`)) {
        try {
          const res = await fetch(url);
          const j = await res.json().catch(() => null);
          if (j && j.ok) {
            setCall((c) => ({ ...(c || {}), ...j }));
            if (TERMINAL.has((j.status || "").toLowerCase())) {
              clearInterval(pollRef.current); pollRef.current = null;
            }
            break;
          }
        } catch (e) { /* try next */ }
      }
    }, 3000);
  };

  const doCall = async () => {
    setConfirmOpen(false);
    setError(""); setBusy("call");
    const { json } = await postFirst("/api/voice-demo/zia-call", { ...form, confirm: true });
    setBusy("");
    if (!json) { setError("Could not reach the server."); return; }
    if (!json.ok) { setError(json.error || "Call failed to place."); return; }
    setCall({ call_sid: json.call_sid, status: json.status || "initiated", dialed: json.dialed });
    if (json.call_sid) pollStatus(json.call_sid);
  };

  const canProceed = form.phone.trim() && form.token.trim();

  return (
    <div className="max-w-3xl mx-auto py-10 px-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center shadow">
          <Bot size={22} className="text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Riya — outbound call console</h1>
          <p className="text-sm text-muted-foreground">Internal · dials a real phone · dry-run by default</p>
        </div>
      </div>

      {/* Tool 1 — personalized outreach link (safe, no credits, no phone) */}
      <LinkGenerator />

      {/* Tool 2 — real outbound sales call */}
      <div className="flex items-center gap-2 mt-2 mb-3">
        <PhoneCall size={16} className="text-primary" />
        <h2 className="font-bold text-foreground">Outbound sales call</h2>
      </div>
      <div className="flex items-start gap-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 p-3 text-sm mb-5">
        <ShieldAlert size={18} className="shrink-0 mt-0.5" />
        <span>This places a real outbound call and uses telephony + AI credits. Always <b>Preview</b> first.
          Placing a call requires the server console token.</span>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Prospect name" value={form.prospect_name} onChange={set("prospect_name")} placeholder="e.g. Rahul" />
        <Field label="Phone (India mobile)" value={form.phone} onChange={set("phone")} placeholder="+91XXXXXXXXXX" />
        <Field label="Your name (referrer)" value={form.referrer_name} onChange={set("referrer_name")} placeholder="e.g. Vishnu" />
        <Field label="Company (optional)" value={form.company} onChange={set("company")} placeholder="e.g. Acme" />
        <Field label="Prospect email (optional — for booking link)" value={form.prospect_email} onChange={set("prospect_email")} placeholder="prospect@company.com" />
        <Field label="Console token" value={form.token} onChange={set("token")} placeholder="ZIA_CONSOLE_TOKEN" type="password" />
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive p-3 text-sm mt-5">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" /> <span>{error}</span>
        </div>
      )}

      <div className="flex items-center gap-3 mt-6">
        <button
          onClick={doPreview} disabled={!form.phone.trim() || busy === "preview"}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-accent transition disabled:opacity-50"
        >
          {busy === "preview" ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />} Preview (dry run)
        </button>
        <button
          onClick={() => setConfirmOpen(true)} disabled={!canProceed || busy === "call"}
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition disabled:opacity-50"
        >
          {busy === "call" ? <Loader2 size={16} className="animate-spin" /> : <PhoneCall size={16} />} Place call
        </button>
      </div>

      {/* Dry-run preview */}
      {preview && preview.dry_run && (
        <div className="rounded-2xl border border-border bg-secondary/30 p-5 mt-6">
          <div className="flex items-center gap-2 text-primary text-xs font-semibold mb-2">
            <Eye size={14} /> DRY RUN — nothing was dialed
          </div>
          <div className="text-sm text-foreground mb-1">Would dial: <b>{preview.would_dial}</b>
            {preview.prospect_name ? <> · {preview.prospect_name}</> : null}</div>
          <div className="text-xs text-muted-foreground mb-3">Booking link on send: {preview.booking_url}</div>
          <div className="text-xs font-semibold text-muted-foreground mb-1">RIYA WILL OPEN AS:</div>
          <pre className="text-xs whitespace-pre-wrap text-foreground/80 bg-background rounded-lg p-3 border border-border max-h-52 overflow-y-auto">
            {preview.script_preview}
          </pre>
        </div>
      )}

      {/* Live call status */}
      {call && (
        <div className="rounded-2xl overflow-hidden shadow-lg mt-6" style={{ backgroundColor: INK }}>
          <div className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Phone size={16} className="text-primary" />
                <span className="font-semibold">{call.dialed || "Call placed"}</span>
              </div>
              <StatusPill status={call.status} />
            </div>
            {call.link_sent && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white">
                <CheckCircle2 size={13} className="text-primary" /> Booking link sent{call.intent ? ` · ${call.intent}` : ""}
              </div>
            )}
            {Array.isArray(call.transcript) && call.transcript.length > 0 && (
              <div className="mt-4 bg-black/30 rounded-lg p-3 max-h-56 overflow-y-auto space-y-2">
                {call.transcript.map((m, i) => (
                  <div key={i} className="text-xs">
                    <span className={m.role === "agent" ? "text-primary font-semibold" : "text-white/60 font-semibold"}>
                      {m.role === "agent" ? "Riya" : "Prospect"}:
                    </span>{" "}
                    <span className="text-white/90">{m.text}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[11px] text-white/40 mt-3">sid: {call.call_sid}</div>
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      {confirmOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setConfirmOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl p-7 shadow-2xl text-white" style={{ backgroundColor: INK }}>
            <button onClick={() => setConfirmOpen(false)} className="absolute right-4 top-4 text-white/50 hover:text-white" aria-label="Close"><X size={20} /></button>
            <div className="w-11 h-11 rounded-xl bg-amber-500 flex items-center justify-center shadow mb-4">
              <PhoneCall size={22} className="text-white" />
            </div>
            <h2 className="text-xl font-bold mb-1">Place a real call?</h2>
            <p className="text-white/70 text-sm mb-4">
              Riya will call <b className="text-white">{form.phone}</b>
              {form.prospect_name ? <> ({form.prospect_name})</> : null} right now. This uses real telephony + AI credits.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmOpen(false)} className="flex-1 rounded-full border border-white/20 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10 transition">Cancel</button>
              <button onClick={doCall} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition">
                <PhoneCall size={15} /> Call now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Personalized-outreach link builder. Produces a /hirewithus/demo link carrying
 * ?name/company/role so Riya greets the prospect by name and researches their
 * company live. `&talk=1` drops them straight into the conversation. No call,
 * no credits — just a URL to paste into LinkedIn/email.
 */
function LinkGenerator() {
  const [f, setF] = useState({ name: "", company: "", role: "" });
  const [copied, setCopied] = useState(false);
  const set = (k) => (e) => { setF((s) => ({ ...s, [k]: e.target.value })); setCopied(false); };
  const hasAny = f.name.trim() || f.company.trim() || f.role.trim();

  const link = useMemo(() => {
    const qs = new URLSearchParams();
    if (f.name.trim()) qs.set("name", f.name.trim());
    if (f.company.trim()) qs.set("company", f.company.trim());
    if (f.role.trim()) qs.set("role", f.role.trim());
    qs.set("talk", "1");
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/hirewithus/demo?${qs.toString()}`;
  }, [f]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked (non-HTTPS) — the field is selectable as a fallback */ }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 mb-8">
      <div className="flex items-center gap-2 mb-1">
        <LinkIcon size={16} className="text-primary" />
        <h2 className="font-bold text-foreground">Personalized demo link</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        For LinkedIn / email outreach. Riya greets the prospect by name and researches their company
        live. Nothing is dialed and no credits are used.
      </p>

      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Prospect name" value={f.name} onChange={set("name")} placeholder="e.g. Rahul" />
        <Field label="Company" value={f.company} onChange={set("company")} placeholder="e.g. Microsoft" />
        <Field label="Role / title" value={f.role} onChange={set("role")} placeholder="e.g. Head of TA" />
      </div>

      <div className="mt-4">
        <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Ready-to-share link</label>
        <div className="flex flex-col sm:flex-row items-stretch gap-2">
          <input
            readOnly value={link} onFocus={(e) => e.target.select()}
            className="flex-1 rounded-xl bg-secondary/40 border border-border px-4 py-2.5 text-sm text-foreground outline-none font-mono truncate"
          />
          <div className="flex gap-2">
            <button
              onClick={copy} disabled={!hasAny}
              className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition disabled:opacity-50"
            >
              {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy</>}
            </button>
            <a
              href={link} target="_blank" rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-accent transition"
            >
              <ExternalLink size={15} /> Open
            </a>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Fill in as much as you can — the more Riya knows, the more tailored her opening. The prospect
          still enters their work email when they start (needed for the booking invite).
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">{label}</label>
      <input
        type={type} value={value} onChange={onChange} placeholder={placeholder}
        className="w-full rounded-xl bg-secondary/40 border border-border px-4 py-2.5 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
      />
    </div>
  );
}

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const live = s === "in_call" || s === "initiated";
  const good = s === "completed";
  const bad = ["error", "failed", "busy", "no-answer"].includes(s);
  const cls = good ? "bg-green-500/20 text-green-300"
    : bad ? "bg-red-500/20 text-red-300"
    : "bg-amber-500/20 text-amber-200";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}>
      <span className={`w-2 h-2 rounded-full ${live ? "bg-amber-400 animate-pulse" : good ? "bg-green-400" : "bg-red-400"}`} />
      {status || "…"}
    </span>
  );
}
